/**
 * puzzleEncrypter — undo the server's puzzle-solution obfuscation.
 *
 * Python twin: `chess/utils/puzzle_crypto.py`.
 * App twin:    `cc-learning-app/src/Utils/puzzleEncrypter.ts`.
 * All three MUST stay byte-compatible — see FORMAT below.
 *
 * WHY THIS EXISTS
 * ---------------
 * Puzzle answers used to arrive as plaintext: `moves` on the
 * `learning/puzzles/by_ids/` response was the full space-joined UCI solution
 * line, so a student with devtools open could read the answers to a live
 * Puzzle Royale race straight out of the network tab. The server now splits it:
 *
 *   setup_ply   the OPPONENT's auto-played first ply, in the CLEAR. Not secret
 *               — it is drawn on the board and highlighted as the last move.
 *               EMPTY for curated no_setup puzzles, which have no setup ply.
 *   moves_enc   the PLAYER's answer plies, obfuscated.
 *
 * WHAT THIS IS NOT
 * ----------------
 * Not real secrecy. The key below is compiled into this bundle — it has to be,
 * because we decrypt with it. Anyone willing to unpack the bundle and read
 * PUZZLE_ENC_KEY can decode everything this protects.
 *
 * So: peeking goes from "open the network tab" (which students stumble into)
 * to "reverse engineer the bundle". Real against casual cheating, worth nothing
 * against a motivated attacker. Named deobfuscate/obfuscate rather than
 * decrypt/encrypt so nobody downstream mistakes it for confidentiality and
 * leans on it for something that needs the real thing. If you ever need an
 * answer a client genuinely cannot recover, the only shape that works is
 * server-side validation — the answer never ships at all.
 *
 * FORMAT
 * ------
 *     "v1:" + base64( plaintext XOR keystream(puzzleId) )
 *
 *   keystream = HMAC-SHA256(key, asciiDecimal(puzzleId) || counterBE32) blocks,
 *   counter from 0, concatenated and truncated to the plaintext length.
 *
 * The puzzle id is the nonce, so the same solution under two different ids
 * yields different ciphertext — without that, a repeated common answer (a
 * frequent mate-in-1) would be spottable by its repeated token alone, no key
 * required.
 *
 * Uses crypto-js HmacSHA256, already a dependency in this repo — no new
 * package, and it matches Python's stdlib hmac/hashlib on the other side. No
 * authentication tag: a tampered token decodes to garbage, which simply fails
 * to match the player's move. Nothing security-relevant rests on the plaintext
 * being authentic.
 *
 * Also ~2x cheaper to undo than AES-CBC (~0.013ms vs ~0.029ms per call), which
 * is why the answer is deobfuscated AT USE (per move) rather than for the whole
 * ~90-puzzle set at fetch: the ciphertext then never sits decoded in the
 * react-query cache, which is `cacheTime: Infinity` and browsable in devtools.
 */
import CryptoJS from 'crypto-js';

import ENV from '../../environment';

/** Version tag the server stamps on every token. */
const PREFIX = 'v1:';

/**
 * Strict base64 shape. crypto-js's Base64 parser is LENIENT — it silently drops
 * characters outside the alphabet instead of throwing, so "%%%" decodes to two
 * zero bytes rather than failing. Those bytes can XOR into printable ASCII and
 * pass the UTF-8 check below, which would hand the walker a garbage "solution"
 * instead of falling back. Validate the shape ourselves before parsing.
 */
const BASE64_RE = /^[A-Za-z0-9+/]+={0,2}$/;

/**
 * A resolved UCI line must look like plies: 4-5 chars of file/rank (+ optional
 * promotion piece), space-separated. Cheap sanity gate so a corrupt token can
 * never masquerade as a solution — a stream cipher has no authentication tag,
 * so this is what stands in for one.
 */
const UCI_LINE_RE = /^[a-h][1-8][a-h][1-8][qrbn]?( [a-h][1-8][a-h][1-8][qrbn]?)*$/;

/**
 * Shared obfuscation key. Read lazily (not at module scope) so a missing env
 * entry can't throw during import — `env.preprod.js` may not carry the key.
 */
function keyWords(): CryptoJS.lib.WordArray | null {
  const raw = (ENV as {PUZZLE_ENC_KEY?: string})?.PUZZLE_ENC_KEY;
  if (!raw || typeof raw !== 'string') {
    return null;
  }
  return CryptoJS.enc.Utf8.parse(raw);
}

/** HMAC-SHA256 keystream: HMAC(key, nonce || counterBE32) blocks, as bytes. */
function keystream(
  key: CryptoJS.lib.WordArray,
  nonce: string,
  length: number,
): number[] {
  const out: number[] = [];
  let counter = 0;
  const nonceWords = CryptoJS.enc.Utf8.parse(nonce);
  while (out.length < length) {
    // 4-byte big-endian counter, matching Python's to_bytes(4, 'big').
    const counterHex = counter.toString(16).padStart(8, '0');
    const message = nonceWords
      .clone()
      .concat(CryptoJS.enc.Hex.parse(counterHex));
    const hex = CryptoJS.HmacSHA256(message, key).toString(CryptoJS.enc.Hex);
    for (let i = 0; i < hex.length; i += 2) {
      out.push(parseInt(hex.substr(i, 2), 16));
    }
    counter += 1;
  }
  return out.slice(0, length);
}

/** True when a key is configured — i.e. tokens can be decoded at all. */
export function isPuzzleEncryptionAvailable(): boolean {
  return keyWords() !== null;
}

/**
 * Decode a `moves_enc` token back to its space-joined UCI answer plies.
 *
 * Returns null for anything unusable — no key, missing/short token, wrong
 * version tag, undecodable base64/UTF-8. Callers MUST treat null as "fall back
 * to the plaintext `moves` field" rather than as "no solution": an older server
 * (or one with no key configured) sends no token at all, and a puzzle whose
 * solution resolves to empty would judge every move WRONG.
 *
 * `puzzleId` must be the id the CLIENT knows — for curated Demo-Call puzzles
 * that is the OFFSET id (+1e9) from coords[], which is what the server used as
 * the nonce.
 */
export function deobfuscatePuzzleMoves(
  token: string | null | undefined,
  puzzleId: number | string,
): string | null {
  const key = keyWords();
  if (!key || !token || typeof token !== 'string') {
    return null;
  }
  if (!token.startsWith(PREFIX)) {
    return null;
  }

  const body = token.slice(PREFIX.length);
  if (!body || !BASE64_RE.test(body)) {
    return null;
  }

  try {
    const words = CryptoJS.enc.Base64.parse(body);
    const hex = words.toString(CryptoJS.enc.Hex);
    if (!hex) {
      return null;
    }
    const cipher: number[] = [];
    for (let i = 0; i < hex.length; i += 2) {
      cipher.push(parseInt(hex.substr(i, 2), 16));
    }
    const stream = keystream(key, String(puzzleId), cipher.length);
    const plain = cipher.map((b, i) => b ^ stream[i]);
    // Build via a WordArray so the app twin can share this code verbatim
    // (no Buffer/TextDecoder dependency under React Native).
    const plainHex = plain
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    const text = CryptoJS.enc.Hex.parse(plainHex).toString(CryptoJS.enc.Utf8);
    if (!text) {
      return null;
    }
    // Final gate: the result must actually look like a UCI ply line. Without an
    // authentication tag, a wrong key / wrong nonce / tampered token decodes to
    // bytes that occasionally survive UTF-8 — returning those would hand the
    // walker a bogus "solution" and judge every move wrong, instead of falling
    // back to plaintext. Cheap, and it makes a decode failure always LOOK like
    // a decode failure.
    return UCI_LINE_RE.test(text) ? text : null;
  } catch {
    return null;
  }
}

/**
 * The OPPONENT's auto-played setup ply, or '' when there isn't one.
 *
 * For callers that only need to reach the position the player SAW — the
 * results-grid previews and the spectator board — and must never touch the
 * answer. Prefers the plaintext `setup_ply` field and falls back to the first
 * ply of the legacy `moves` line for older servers.
 *
 * Returns '' for curated `no_setup` puzzles: their FEN is already the position
 * the player faces, so there is nothing to auto-play, and their first ply is
 * the PLAYER's move (taking it would leak the answer — for a Demo_B mate-in-1
 * it would BE the answer).
 */
export function resolveSetupPly(puzzle: {
  moves?: string;
  setup_ply?: string;
  no_setup?: boolean;
}): string {
  if (!puzzle || puzzle.no_setup) {
    return '';
  }
  const explicit = (puzzle.setup_ply || '').trim();
  if (explicit) {
    return explicit;
  }
  // Legacy server: no setup_ply field, so take it off the plaintext line.
  return (puzzle.moves || '').split(/\s+/).filter(Boolean)[0] || '';
}

/**
 * Reassemble the full solution line the way the walker expects it:
 * `[setup_ply, ...answer plies]`, space-joined.
 *
 * Resolution order, and why:
 *   1. `moves_enc` — preferred; the obfuscated answer.
 *   2. `moves`     — plaintext fallback. The server still sends it for older
 *                    app builds an OTA cannot reach. Once the active-version
 *                    floor is past this build the server can drop it, and this
 *                    branch becomes dead (see the Phase 3 note in
 *                    `get_puzzles_by_ids`).
 *
 * The no_setup case is the one that needs care. Curated Demo-Call puzzles have
 * NO opponent setup ply — the FEN is already the player's turn and the first
 * ply is the PLAYER's — so `setup_ply` is empty and the decoded answer IS the
 * whole line. Prefixing anything there would corrupt the line, and for a
 * Demo_B mate-in-1 the "setup" would have been the entire solution. We key off
 * an empty `setup_ply` rather than the `no_setup` flag so a bad-data row
 * (no_setup false but no setup ply) degrades to "no prefix" instead of
 * producing a line with a stray leading space.
 */
export function resolvePuzzleMoves(puzzle: {
  id?: number | string;
  moves?: string;
  moves_enc?: string;
  setup_ply?: string;
}): string {
  if (!puzzle) {
    return '';
  }

  const id = puzzle.id ?? '';
  const answer = deobfuscatePuzzleMoves(puzzle.moves_enc, id);

  if (answer === null) {
    // No token / no key / undecodable → plaintext path (older server).
    return (puzzle.moves || '').trim();
  }

  const setup = (puzzle.setup_ply || '').trim();
  if (!setup) {
    return answer.trim();
  }
  return `${setup} ${answer}`.trim();
}
